import React, { useState, useCallback, useRef } from 'react';
import { Search, Loader2, Pill, X } from 'lucide-react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { searchMedications, client } from '../../services/api';
import type { MedicationResponse } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

interface MedicationSearchProps {
  value?: MedicationResponse | null;
  onSelect?: (medication: MedicationResponse) => void;
  onClear?: () => void;
  onAddOffMarket?: (searchTerm: string) => void;
  placeholder?: string;
}

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query || query.length < 2) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <span className='font-bold'>{match}</span>
      {after}
    </>
  );
};

const MedicationSearch: React.FC<MedicationSearchProps> = ({
  value,
  onSelect,
  onClear,
  onAddOffMarket,
  placeholder = 'Search medications...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<MedicationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // If there's a selected value, show it in the input
  const displayValue = value ? value.full_name : searchTerm;

  // Filter state
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const [availableForms, setAvailableForms] = useState<string[]>([]);
  const [availableRoutes, setAvailableRoutes] = useState<string[]>([]);

  const performSearch = useCallback(
    async (term: string, form?: string, route?: string) => {
      if (!term || term.length < 2) {
        setResults([]);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await searchMedications({
          query: {
            search: term,
            limit: 10,
            form: form || undefined,
            route: route || undefined,
          },
          client,
        });

        if (response.data) {
          setResults(response.data.items);
          setTotalCount(response.data.total);
          setAvailableForms(response.data.available_forms ?? []);
          setAvailableRoutes(response.data.available_routes ?? []);
        }
      } catch (error) {
        console.error('Error searching medications:', error);
        setResults([]);
        setTotalCount(0);
        setAvailableForms([]);
        setAvailableRoutes([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const debouncedSearch = useDebounce(
    (term: string, form: string, route: string) =>
      performSearch(term, form, route),
    300
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 2) {
      setOpen(true);
      setIsLoading(true);
      debouncedSearch(value, selectedForm, selectedRoute);
    } else {
      setResults([]);
      setTotalCount(0);
      setOpen(false);
      // Clear filters when search is cleared
      setSelectedForm('');
      setSelectedRoute('');
      setAvailableForms([]);
      setAvailableRoutes([]);
    }
  };

  const clearFilters = () => {
    setSelectedForm('');
    setSelectedRoute('');
    if (searchTerm.length >= 2) {
      setIsLoading(true);
      performSearch(searchTerm, '', '');
    }
  };

  const hasActiveFilters = selectedForm !== '' || selectedRoute !== '';

  const handleFormChange = (value: string) => {
    const newForm = value === 'all' ? '' : value;
    setSelectedForm(newForm);
    if (searchTerm.length >= 2) {
      setIsLoading(true);
      performSearch(searchTerm, newForm, selectedRoute);
    }
  };

  const handleRouteChange = (value: string) => {
    const newRoute = value === 'all' ? '' : value;
    setSelectedRoute(newRoute);
    if (searchTerm.length >= 2) {
      setIsLoading(true);
      performSearch(searchTerm, selectedForm, newRoute);
    }
  };

  const handleSelect = (medication: MedicationResponse) => {
    if (onSelect) {
      onSelect(medication);
    }
    setSearchTerm('');
    setResults([]);
    setOpen(false);
  };

  const handleFocus = () => {
    // Don't open dropdown if a medication is selected
    if (!value && searchTerm.length >= 2) {
      setOpen(true);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setOpen(false);
    if (onClear) {
      onClear();
    }
    // Focus the input after clearing
    inputRef.current?.focus();
  };

  const showDropdown = open && searchTerm.length >= 2 && !value;

  return (
    <Popover open={showDropdown} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className='relative w-full'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10' />
          <Input
            ref={inputRef}
            type='text'
            placeholder={placeholder}
            value={displayValue}
            onChange={e => {
              // If there's a selected value and user starts typing, clear it first
              if (value && onClear) {
                onClear();
              }
              handleInputChange(e);
            }}
            onFocus={handleFocus}
            className='pl-9 pr-9'
            inputSize='default'
          />
          {isLoading && !value && (
            <Loader2 className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground' />
          )}
          {value && (
            <Button
              variant='ghost'
              size='sm'
              className='absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0'
              onClick={handleClear}
            >
              <X className='h-4 w-4 text-muted-foreground' />
            </Button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className='p-0 w-[var(--radix-popover-trigger-width)]'
        align='start'
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {results.length > 0 ? (
          <ul className='max-h-[300px] overflow-auto py-1'>
            {results.map(medication => (
              <li key={medication.id}>
                <button
                  type='button'
                  className='w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none'
                  onClick={() => handleSelect(medication)}
                >
                  <div className='flex items-start gap-2'>
                    <Pill className='mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground' />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm truncate'>
                        {highlightMatch(medication.full_name, searchTerm)}
                      </div>
                      <div className='text-xs text-muted-foreground flex flex-wrap gap-x-2'>
                        {medication.generic_name && (
                          <span>
                            {highlightMatch(
                              medication.generic_name,
                              searchTerm
                            )}
                          </span>
                        )}
                        {medication.therapeutic_category && (
                          <span className='text-muted-foreground/70'>
                            {medication.therapeutic_category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : !isLoading ? (
          <div className='px-3 py-6 text-center text-sm text-muted-foreground'>
            <p>No medications found for "{searchTerm}"</p>
            {hasActiveFilters && (
              <Button
                variant='outline'
                size='sm'
                className='mt-2 mx-auto block'
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
            {onAddOffMarket && (
              <Button
                variant='outline'
                size='sm'
                className='mt-2 mx-auto block'
                onClick={() => {
                  onAddOffMarket(searchTerm);
                  setSearchTerm('');
                  setOpen(false);
                }}
              >
                Add Off Market / Unlisted Medication
              </Button>
            )}
          </div>
        ) : null}

        {/* Filters - always visible */}
        {!isLoading && (
          <div className='border-t px-3 py-2'>
            <div className='flex items-center justify-between gap-2'>
              <span className='text-xs text-muted-foreground'>
                {results.length > 0
                  ? `Showing ${results.length} of ${totalCount} results`
                  : 'Filter results'}
              </span>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-1'>
                  <Select
                    value={selectedForm || 'all'}
                    onValueChange={handleFormChange}
                  >
                    <SelectTrigger
                      className='h-7 w-24 text-xs'
                      onClick={e => e.stopPropagation()}
                    >
                      <SelectValue placeholder='Form' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All forms</SelectItem>
                      {availableForms.map(form => (
                        <SelectItem key={form} value={form}>
                          {form}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedForm && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-5 w-5 p-0'
                      onClick={e => {
                        e.stopPropagation();
                        handleFormChange('all');
                      }}
                    >
                      <X className='h-3 w-3 text-muted-foreground' />
                    </Button>
                  )}
                </div>
                <div className='flex items-center gap-1'>
                  <Select
                    value={selectedRoute || 'all'}
                    onValueChange={handleRouteChange}
                  >
                    <SelectTrigger
                      className='h-7 w-24 text-xs'
                      onClick={e => e.stopPropagation()}
                    >
                      <SelectValue placeholder='Route' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All routes</SelectItem>
                      {availableRoutes.map(route => (
                        <SelectItem key={route} value={route}>
                          {route}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRoute && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-5 w-5 p-0'
                      onClick={e => {
                        e.stopPropagation();
                        handleRouteChange('all');
                      }}
                    >
                      <X className='h-3 w-3 text-muted-foreground' />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MedicationSearch;
